import { formatMoney, fromMinorUnits, parseCivilDate } from '@klopotun/domain';
import { useState } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { useController, useWatch, type Control } from 'react-hook-form';
import { Button, TextField } from '../../../../shared/ui/index.js';
import { formatMonthName } from '../../../payment-view/index.js';
import { toAmountMinor } from '../../lib/payment-form-adapter.js';
import {
  PAST_PAYMENT_STATE,
  type PastPaymentDraft,
  type PastPaymentState,
  type PaymentFormValues,
} from '../../model/payment-form-values.js';
import styles from './history-step.module.css';
import { useRangeSelection } from './use-range-selection.js';

const MONTH_SHORT_NAMES = [
  'січ',
  'лют',
  'бер',
  'кві',
  'тра',
  'чер',
  'лип',
  'сер',
  'вер',
  'жов',
  'лис',
  'гру',
];

const WHOLE_UNITS_SUFFIX = /,00$/;

const AMOUNT_ERROR = 'Вкажіть суму більше нуля, наприклад 149.00';

type HistoryCell = {
  draft: PastPaymentDraft;
  index: number;
  month: number;
  year: number;
};

type HistoryYear = {
  year: number;
  cells: HistoryCell[];
};

const yearsOf = (history: PastPaymentDraft[]): HistoryYear[] =>
  history.reduce<HistoryYear[]>((years, draft, index) => {
    const date = parseCivilDate(draft.dueDate);
    const cell = { draft, index, month: date.month, year: date.year };
    const last = years.at(-1);

    if (last !== undefined && last.year === date.year) {
      return [...years.slice(0, -1), { year: last.year, cells: [...last.cells, cell] }];
    }

    return [...years, { year: date.year, cells: [cell] }];
  }, []);

const formatCellAmount = (amountMinor: number, currency: string): string =>
  formatMoney({ amountMinor, currency }, { symbol: '', symbolPosition: 'before' })
    .trim()
    .replace(WHOLE_UNITS_SUFFIX, '');

const STATE_CLASS: Record<PastPaymentState, string | undefined> = {
  [PAST_PAYMENT_STATE.PAID]: styles.paid,
  [PAST_PAYMENT_STATE.OWED]: styles.owed,
  [PAST_PAYMENT_STATE.SKIPPED]: styles.skipped,
};

type CellButtonProps = {
  cell: HistoryCell;
  amountMinor: number | null;
  currency: string;
  isSelected: boolean;
  onClick: (index: number, event: MouseEvent<HTMLButtonElement>) => void;
};

const valueTextOf = (
  state: PastPaymentState,
  amountMinor: number | null,
  currency: string,
): string => {
  if (state === PAST_PAYMENT_STATE.OWED) {
    return 'борг';
  }

  if (state === PAST_PAYMENT_STATE.SKIPPED) {
    return '—';
  }

  return amountMinor === null ? '?' : formatCellAmount(amountMinor, currency);
};

const spokenStateOf = (
  state: PastPaymentState,
  amountMinor: number | null,
  currency: string,
): string => {
  if (state === PAST_PAYMENT_STATE.OWED) {
    return 'ще не оплачено';
  }

  if (state === PAST_PAYMENT_STATE.SKIPPED) {
    return 'не було платежу';
  }

  return amountMinor === null
    ? 'оплачено, сума не вказана'
    : `оплачено ${formatMoney({ amountMinor, currency })}`;
};

const CellButton = ({ cell, amountMinor, currency, isSelected, onClick }: CellButtonProps) => (
  <button
    type="button"
    data-index={cell.index}
    className={[styles.cell, STATE_CLASS[cell.draft.state], isSelected ? styles.selected : '']
      .filter((name) => name !== undefined && name !== '')
      .join(' ')}
    aria-pressed={isSelected}
    aria-label={`${formatMonthName(cell.month)} ${String(cell.year)}: ${spokenStateOf(
      cell.draft.state,
      amountMinor,
      currency,
    )}`}
    onClick={(event) => {
      onClick(cell.index, event);
    }}
  >
    <span className={styles.month}>{MONTH_SHORT_NAMES[cell.month - 1]}</span>
    <span className={styles.value}>{valueTextOf(cell.draft.state, amountMinor, currency)}</span>
  </button>
);

type SelectionPanelProps = {
  count: number;
  amountText: string;
  amountPlaceholder: string;
  amountError: string | null;
  onAmountChange: (value: string) => void;
  onApplyAmount: () => void;
  onMarkOwed: () => void;
  onMarkSkipped: () => void;
  onClear: () => void;
};

const SelectionPanel = ({
  count,
  amountText,
  amountPlaceholder,
  amountError,
  onAmountChange,
  onApplyAmount,
  onMarkOwed,
  onMarkSkipped,
  onClear,
}: SelectionPanelProps) => {
  // Enter in the amount field would otherwise submit the whole subscription form.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    onApplyAmount();
  };

  return (
    <div
      className={styles.panel}
      role="group"
      aria-label="Дії з обраними"
      onKeyDown={handleKeyDown}
    >
      <p className={styles.panelTitle}>Обрано: {count}</p>

      <div className={styles.amountRow}>
        <div className={styles.amountField}>
          <TextField
            label="Сума для обраних"
            name="historyAmount"
            value={amountText}
            inputMode="decimal"
            placeholder={amountPlaceholder}
            error={amountError ?? undefined}
            onChange={(event) => {
              onAmountChange(event.target.value);
            }}
          />
        </div>
        <Button onClick={onApplyAmount}>Застосувати</Button>
      </div>

      <div className={styles.panelActions}>
        <Button variant="secondary" onClick={onMarkOwed}>
          Ще не оплачено
        </Button>
        <Button variant="secondary" onClick={onMarkSkipped}>
          Не було платежу
        </Button>
        <Button variant="secondary" onClick={onClear}>
          Скинути вибір
        </Button>
      </div>
    </div>
  );
};

type HistoryStepProps = {
  control: Control<PaymentFormValues>;
};

export const HistoryStep = ({ control }: HistoryStepProps) => {
  const [amountText, setAmountText] = useState('');
  const [amountError, setAmountError] = useState<string | null>(null);
  const { field, fieldState } = useController({ control, name: 'history' });
  const [subscriptionAmount, currency] = useWatch({
    control,
    name: ['amount', 'currency'],
  });
  const selection = useRangeSelection();

  const history = field.value;
  const amountMinorOf = (draft: PastPaymentDraft): number | null =>
    toAmountMinor(draft.amount ?? subscriptionAmount, currency);
  const paidAmounts = history
    .filter((draft) => draft.state === PAST_PAYMENT_STATE.PAID)
    .map(amountMinorOf)
    .filter((amountMinor): amountMinor is number => amountMinor !== null);
  const paidTotal = paidAmounts.reduce((sum, amountMinor) => sum + amountMinor, 0);
  const subscriptionMinor = toAmountMinor(subscriptionAmount, currency);

  const applyToSelected = (patch: Partial<PastPaymentDraft>): void => {
    field.onChange(
      history.map((draft, index) =>
        selection.selected.has(index) ? { ...draft, ...patch, isEdited: true } : draft,
      ),
    );
    selection.clear();
  };

  const applyAmount = (): void => {
    const amountMinor = toAmountMinor(amountText, currency);

    if (amountMinor === null || amountMinor <= 0) {
      setAmountError(AMOUNT_ERROR);
      return;
    }

    applyToSelected({
      state: PAST_PAYMENT_STATE.PAID,
      amount: fromMinorUnits(amountMinor, currency),
    });
    setAmountText('');
    setAmountError(null);
  };

  return (
    <div className={styles.step}>
      <p className={styles.caption}>
        Суми в {currency}. Дотик додає платіж до вибору, протягування пальцем обирає кілька підряд.
      </p>

      <div className={styles.summaryRow}>
        <p className={styles.summary}>
          Оплачено {paidAmounts.length} з {history.length} · разом{' '}
          {formatMoney({ amountMinor: paidTotal, currency })}
        </p>
        <button
          type="button"
          className={styles.selectAll}
          onClick={() => {
            selection.selectAll(history.length);
          }}
        >
          Обрати всі
        </button>
      </div>

      <div className={styles.grid} {...selection.gridHandlers}>
        {yearsOf(history).map((year) => (
          <section key={year.year} className={styles.year} aria-label={String(year.year)}>
            <p className={styles.yearLabel}>{year.year}</p>
            <div className={styles.cells}>
              {year.cells.map((cell) => (
                <CellButton
                  key={cell.draft.dueDate}
                  cell={cell}
                  amountMinor={amountMinorOf(cell.draft)}
                  currency={currency}
                  isSelected={selection.selected.has(cell.index)}
                  onClick={selection.onCellClick}
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      {fieldState.error?.message !== undefined && (
        <p className={styles.error} role="alert">
          {fieldState.error.message}
        </p>
      )}

      {selection.selected.size > 0 && (
        <SelectionPanel
          count={selection.selected.size}
          amountText={amountText}
          amountPlaceholder={
            subscriptionMinor === null ? '149.00' : fromMinorUnits(subscriptionMinor, currency)
          }
          amountError={amountError}
          onAmountChange={(value) => {
            setAmountText(value);
            setAmountError(null);
          }}
          onApplyAmount={applyAmount}
          onMarkOwed={() => {
            applyToSelected({ state: PAST_PAYMENT_STATE.OWED });
          }}
          onMarkSkipped={() => {
            applyToSelected({ state: PAST_PAYMENT_STATE.SKIPPED });
          }}
          onClear={selection.clear}
        />
      )}
    </div>
  );
};
